import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AssignableMember,
  CreateTicketInput,
  CreateTicketStatusInput,
  CreateTicketTypeGroupInput,
  CreateTicketTypeInput,
  PaginatedResult,
  SortDirection,
  Ticket,
  TicketDiagnostics,
  TicketOrigin,
  TicketPriority,
  TicketStatus,
  TicketType,
  TicketTypeGroup,
  UpdateTicketInput,
  UpdateTicketStatusInput,
  UpdateTicketTypeGroupInput,
  UpdateTicketTypeInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

// ---------------------------------------------------------------------------
// Tickets
// ---------------------------------------------------------------------------

export interface TicketListParams {
  page: number;
  pageSize: number;
  search?: string;
  origin?: TicketOrigin;
  projectId?: string;
  customerId?: string;
  phaseId?: string;
  agreementId?: string;
  ticketTypeId?: string;
  ticketTypeGroupId?: string;
  statusId?: string;
  priority?: TicketPriority;
  assigneeMemberId?: string;
  assigneeMemberIds?: string[];
  open?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const ticketKeys = {
  all: ['tickets'] as const,
  list: (params: TicketListParams) => ['tickets', 'list', params] as const,
  detail: (id: string) => ['tickets', 'detail', id] as const,
  diagnostics: (id: string) => ['tickets', 'diagnostics', id] as const,
  assignableMembers: ['tickets', 'assignable-members'] as const,
};

export function useTickets(params: TicketListParams): UseQueryResult<PaginatedResult<Ticket>> {
  return useQuery({
    queryKey: ticketKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<Ticket>>(
        `/tickets${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          origin: params.origin,
          projectId: params.projectId,
          customerId: params.customerId,
          phaseId: params.phaseId,
          agreementId: params.agreementId,
          ticketTypeId: params.ticketTypeId,
          ticketTypeGroupId: params.ticketTypeGroupId,
          statusId: params.statusId,
          priority: params.priority,
          assigneeMemberId: params.assigneeMemberId,
          assigneeMemberIds: params.assigneeMemberIds?.join(','),
          open: params.open,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useTicket(id: string | null): UseQueryResult<Ticket> {
  return useQuery({
    queryKey: ticketKeys.detail(id ?? 'none'),
    enabled: Boolean(id),
    queryFn: () => api.get<Ticket>(`/tickets/${id ?? ''}`),
  });
}

export function useTicketDiagnostics(
  id: string | null,
  enabled = true,
): UseQueryResult<TicketDiagnostics> {
  return useQuery({
    queryKey: ticketKeys.diagnostics(id ?? 'none'),
    enabled: enabled && Boolean(id),
    queryFn: () => api.get<TicketDiagnostics>(`/tickets/${id ?? ''}/diagnostics`),
  });
}

export function useAssignableMembers(): UseQueryResult<AssignableMember[]> {
  return useQuery({
    queryKey: ticketKeys.assignableMembers,
    queryFn: () => api.get<AssignableMember[]>('/tickets/assignable-members'),
  });
}

/** Active ticket statuses for the ticket form (any tickets user). */
export function useTicketStatusOptions(): UseQueryResult<TicketStatus[]> {
  return useQuery({
    queryKey: ['tickets', 'status-options'] as const,
    queryFn: () => api.get<TicketStatus[]>('/tickets/status-options'),
  });
}

/** Active ticket types for the support-ticket form (any tickets user). */
export function useTicketTypeOptions(): UseQueryResult<TicketType[]> {
  return useQuery({
    queryKey: ['tickets', 'type-options'] as const,
    queryFn: () => api.get<TicketType[]>('/tickets/type-options'),
  });
}

export function useCreateTicket(): UseMutationResult<Ticket, unknown, CreateTicketInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTicketInput) => api.post<Ticket>('/tickets', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export interface UpdateTicketArgs {
  id: string;
  input: UpdateTicketInput;
}

export function useUpdateTicket(): UseMutationResult<Ticket, unknown, UpdateTicketArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateTicketArgs) => api.patch<Ticket>(`/tickets/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useDeleteTicket(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/tickets/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useRegenerateTicketPublicLink(): UseMutationResult<Ticket, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Ticket>(`/tickets/${id}/public-link/regenerate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Ticket statuses (admin)
// ---------------------------------------------------------------------------

export interface TicketStatusListParams {
  page: number;
  pageSize: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const ticketStatusKeys = {
  all: ['ticket-statuses'] as const,
  list: (params: TicketStatusListParams) => ['ticket-statuses', 'list', params] as const,
};

export function useTicketStatuses(
  params: TicketStatusListParams,
): UseQueryResult<PaginatedResult<TicketStatus>> {
  return useQuery({
    queryKey: ticketStatusKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<TicketStatus>>(
        `/ticket-statuses${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          isActive: params.isActive,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useCreateTicketStatus(): UseMutationResult<
  TicketStatus,
  unknown,
  CreateTicketStatusInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTicketStatusInput) =>
      api.post<TicketStatus>('/ticket-statuses', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketStatusKeys.all });
    },
  });
}

export interface UpdateTicketStatusArgs {
  id: string;
  input: UpdateTicketStatusInput;
}

export function useUpdateTicketStatus(): UseMutationResult<
  TicketStatus,
  unknown,
  UpdateTicketStatusArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateTicketStatusArgs) =>
      api.patch<TicketStatus>(`/ticket-statuses/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketStatusKeys.all });
    },
  });
}

export function useDeleteTicketStatus(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/ticket-statuses/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketStatusKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Ticket type groups (admin)
// ---------------------------------------------------------------------------

export interface TicketTypeGroupListParams {
  page: number;
  pageSize: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const ticketTypeGroupKeys = {
  all: ['ticket-type-groups'] as const,
  list: (params: TicketTypeGroupListParams) => ['ticket-type-groups', 'list', params] as const,
};

export function useTicketTypeGroups(
  params: TicketTypeGroupListParams,
): UseQueryResult<PaginatedResult<TicketTypeGroup>> {
  return useQuery({
    queryKey: ticketTypeGroupKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<TicketTypeGroup>>(
        `/ticket-type-groups${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          isActive: params.isActive,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useCreateTicketTypeGroup(): UseMutationResult<
  TicketTypeGroup,
  unknown,
  CreateTicketTypeGroupInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTicketTypeGroupInput) =>
      api.post<TicketTypeGroup>('/ticket-type-groups', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketTypeGroupKeys.all });
    },
  });
}

export interface UpdateTicketTypeGroupArgs {
  id: string;
  input: UpdateTicketTypeGroupInput;
}

export function useUpdateTicketTypeGroup(): UseMutationResult<
  TicketTypeGroup,
  unknown,
  UpdateTicketTypeGroupArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateTicketTypeGroupArgs) =>
      api.patch<TicketTypeGroup>(`/ticket-type-groups/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketTypeGroupKeys.all });
    },
  });
}

export function useDeleteTicketTypeGroup(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/ticket-type-groups/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketTypeGroupKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Ticket types (admin)
// ---------------------------------------------------------------------------

export interface TicketTypeListParams {
  page: number;
  pageSize: number;
  search?: string;
  groupId?: string;
  isActive?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const ticketTypeKeys = {
  all: ['ticket-types'] as const,
  list: (params: TicketTypeListParams) => ['ticket-types', 'list', params] as const,
};

export function useTicketTypes(
  params: TicketTypeListParams,
): UseQueryResult<PaginatedResult<TicketType>> {
  return useQuery({
    queryKey: ticketTypeKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<TicketType>>(
        `/ticket-types${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          groupId: params.groupId,
          isActive: params.isActive,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useCreateTicketType(): UseMutationResult<
  TicketType,
  unknown,
  CreateTicketTypeInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTicketTypeInput) => api.post<TicketType>('/ticket-types', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketTypeKeys.all });
    },
  });
}

export interface UpdateTicketTypeArgs {
  id: string;
  input: UpdateTicketTypeInput;
}

export function useUpdateTicketType(): UseMutationResult<
  TicketType,
  unknown,
  UpdateTicketTypeArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateTicketTypeArgs) =>
      api.patch<TicketType>(`/ticket-types/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketTypeKeys.all });
    },
  });
}

export function useDeleteTicketType(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/ticket-types/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ticketTypeKeys.all });
    },
  });
}
